import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  UNLEASH_ANAYTICS_PAYLOAD,
  UNLEASH_FEATURE_FLAGS,
  UNLEASH_VARIANTS_KEYS,
} from 'src/constants/type';
import { Client } from 'src/lib/api-client';
import { getTimestampTopic } from 'src/lib/fetchers/getTimestampTopic';
import { convertSecondsToHHMMSSFormat } from 'src/shared/core/video/poll-widget/Utils';
import { convertHHMMSSTimeToSeconds } from 'src/utility';
type Props = {
  data: TimeStampTopic[];
  isLoading: boolean;
  refetch: () => void;
  error: any;
  isSuccess: boolean;
};

const handleAnalytics = (payload: UNLEASH_ANAYTICS_PAYLOAD) => {
  const BASE_URL = process.env.REACT_APP_ANALYTICS_API_BASE_URL;

  Client.post(`${BASE_URL}core-analytics/universal/experiments/record`, payload, {
    endPointWithBaseUrl: true,
  });
};

const useGetTimeStampTopic = ({
  scheduleId,
  enabled,
  playerTotalDuration,
}: {
  scheduleId: string;
  enabled: boolean;
  playerTotalDuration: number;
}): Props => {
  const { data, isLoading, refetch, error, isSuccess } = useQuery(
    [
      'getTimeStampTopic' + scheduleId,
      {
        scheduleId,
      },
    ],
    getTimestampTopic,
    {
      enabled,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 60 * 24,
    },
  );

  const payload: UNLEASH_ANAYTICS_PAYLOAD = {
    variantName: UNLEASH_VARIANTS_KEYS.TEST_GROUP,
    experimentName: UNLEASH_FEATURE_FLAGS.TOPIC_WISE_TIMELINE,
    featureEnabled: true,
  };

  const newData = useMemo(() => {
    const timeStampList = (data?.data?.data as any) || [];
    const result = [];
    if (
      !data?.data?.data &&
      (data?.data?.variantName === UNLEASH_VARIANTS_KEYS.DISABLED ||
        data?.data?.variantName === UNLEASH_VARIANTS_KEYS.CONTROL_GROUP)
    ) {
      if (isSuccess) {
        payload.variantName = data?.data?.variantName;
        payload.featureEnabled = false;
        handleAnalytics(payload);
      }
      return [];
    }
    const arrayLength = timeStampList.length || 0;
    if (isSuccess) {
      handleAnalytics(payload);
    }

    // mapping endTime to the next startTime
    for (let i = 0; i < arrayLength; i++) {
      const currentItem = { ...timeStampList[i] };
      const nextItem = timeStampList[i + 1];
      if (i === 0) {
        currentItem.startTime = '00:00:00';
      }
      if (i === arrayLength - 1) {
        currentItem.endTime = convertSecondsToHHMMSSFormat(Math.floor(playerTotalDuration));
      }

      if (nextItem) {
        const nextStartTime = nextItem.startTime;
        const nextStartTimeInSeconds = convertHHMMSSTimeToSeconds(nextStartTime);
        const nextStartTimeFormatted = new Date(nextStartTimeInSeconds * 1000)
          .toISOString()
          .substr(11, 8);
        currentItem.endTime = nextStartTimeFormatted;
      }
      currentItem.startTimeDuration = convertHHMMSSTimeToSeconds(currentItem.startTime);
      currentItem.endTimeDuration = convertHHMMSSTimeToSeconds(currentItem.endTime);
      currentItem.totalDuration = currentItem.endTimeDuration - currentItem.startTimeDuration;
      currentItem.index = i;
      currentItem.cumulativeDuration =
        i === 0
          ? currentItem.totalDuration
          : result[i - 1].cumulativeDuration + currentItem.totalDuration;
      console.log(
        'playerWrapper---',
        `currentItem-${i}`,
        currentItem,
        'lastIndex',
        timeStampList[i - 1]?.cumulativeDuration,
      );
      result.push(currentItem);
    }

    return result.map((item: any) => new TimeStampTopic(item));
  }, [data]);

  return {
    data: newData,
    isLoading,
    refetch,
    error,
    isSuccess,
  };
};

export default useGetTimeStampTopic;

export class TimeStampTopic {
  index: number;
  _id: string;
  scheduleId: string;
  batchId: string;
  subtopicTitle: string;
  startTime: string;
  endTime: string;
  thumbnail: string;
  sortNumber: number;
  startTimeDuration: number;
  endTimeDuration: number;
  isQuestion: boolean;
  totalDuration: number;
  cumulativeDuration: number;

  constructor(data: any) {
    data = data || {};
    this._id = data?._id || '';
    this.scheduleId = data?.scheduleId || '';
    this.batchId = data?.batchId || '';
    this.subtopicTitle = data?.subtopicTitle || '';
    this.startTime = data?.startTime || '';
    this.endTime = data?.endTime || '';
    this.thumbnail = data?.thumbnail?.baseUrl + data?.thumbnail?.key || '';
    this.sortNumber = data?.sortNumber || 0;
    this.startTimeDuration =
      data?.startTimeDuration || convertHHMMSSTimeToSeconds(data?.startTime || '');
    this.endTimeDuration = data?.endTimeDuration || convertHHMMSSTimeToSeconds(data?.endTime || '');
    this.totalDuration = data?.totalDuration || this.endTimeDuration - this.startTimeDuration;
    this.isQuestion = data?.isQuestion || false;
    this.cumulativeDuration = data?.cumulativeDuration || 0;
    this.index = data?.index || 0;
  }
}
